import { TAXONOMY_DISPLAY } from '@archetelos/lib';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRequireRole } from '@/lib/auth/useRequireRole';
import { contentQueries } from '@/lib/queries/content';

export const Route = createFileRoute('/creator/analytics')({
  component: AnalyticsRoute,
});

const OXBLOOD = '#722F37';
const SLATE = '#8d99ae';
const NAVY = '#2b2d42';

type Range = 'all' | 7 | 30 | 90;

function AnalyticsRoute() {
  const { loading } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  // All time (the lifetime matview) is the default — cheapest read, today's
  // behavior. A day-window swaps to the always-live windowed RPC (Spec 26).
  const [range, setRange] = useState<Range>('all');
  const analytics = useQuery(contentQueries.analytics(range));

  if (loading) return null;
  const rows = analytics.data ?? [];

  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <h1 className="font-serif text-3xl text-ink">Analytics</h1>
      <RangeSelector value={range} onChange={setRange} />
    </div>
  );

  if (analytics.isPending) {
    return (
      <div className="mx-auto max-w-5xl">
        {header}
        <p className="mt-8 font-sans text-sm text-ink/50">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="mx-auto max-w-5xl">
        {header}
        <div className="mt-8 rounded-lg border border-ink/10 bg-parchment p-8 text-center">
          {range === 'all' ? (
            <>
              <p className="font-serif text-lg text-ink">Nothing to chart yet</p>
              <p className="mt-3 font-sans text-sm text-ink/70">
                Analytics will appear once you publish your first piece and it&apos;s read. Until
                then, this page will show readers over time, time-spent by content type, and which
                traditions resonate most.
              </p>
            </>
          ) : (
            <>
              <p className="font-serif text-lg text-ink">No reads in the last {range} days</p>
              <p className="mt-3 font-sans text-sm text-ink/70">
                Nothing was read in this window. Try a longer range or All time.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const byType = [
    {
      type: 'Articles',
      seconds: rows
        .filter((r) => r.type === 'article')
        .reduce((s, r) => s + Number(r.total_seconds_spent ?? 0), 0),
    },
    {
      type: 'Videos',
      seconds: rows
        .filter((r) => r.type === 'video')
        .reduce((s, r) => s + Number(r.total_seconds_spent ?? 0), 0),
    },
  ];

  const traditionMap = new Map<string, number>();
  for (const r of rows) {
    for (const t of r.tradition_tags ?? []) {
      traditionMap.set(t, (traditionMap.get(t) ?? 0) + Number(r.total_seconds_spent ?? 0));
    }
  }
  const traditionData = Array.from(traditionMap.entries()).map(([tag, value]) => ({
    name: TAXONOMY_DISPLAY[tag] ?? tag,
    value,
  }));
  const pieColors = [OXBLOOD, SLATE, NAVY, '#d90429', '#8d99ae', '#1F1F1F'];

  const totalReaders = rows.reduce((s, r) => s + Number(r.unique_readers ?? 0), 0);
  const totalSeconds = rows.reduce((s, r) => s + Number(r.total_seconds_spent ?? 0), 0);
  const avgSession = rows.length
    ? Math.round(rows.reduce((s, r) => s + Number(r.avg_seconds_per_session ?? 0), 0) / rows.length)
    : 0;

  return (
    <div className="mx-auto max-w-5xl">
      {header}

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <Card label="Total readers" value={String(totalReaders)} />
        <Card label="Total time" value={`${(totalSeconds / 3600).toFixed(1)} h`} />
        <Card label="Avg session" value={`${avgSession}s`} />
      </div>

      <section className="mt-10">
        <h2 className="font-serif text-lg text-ink">Time spent by content type</h2>
        <div className="mt-4 h-64 w-full">
          <ResponsiveContainer>
            <BarChart data={byType}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F11" />
              <XAxis dataKey="type" stroke={NAVY} fontSize={12} />
              <YAxis stroke={NAVY} fontSize={12} />
              <Tooltip />
              <Bar dataKey="seconds" fill={OXBLOOD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {traditionData.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-lg text-ink">Engagement by tradition</h2>
          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={traditionData} dataKey="value" nameKey="name" outerRadius={110} label>
                  {traditionData.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-serif text-lg text-ink">All content</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-ink/10 font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Readers</th>
                <th className="py-2 pr-4">Total time</th>
                <th className="py-2 pr-4">Avg session</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.content_id} className="border-b border-ink/5">
                  <td className="py-2 pr-4 font-serif text-ink">{r.title}</td>
                  <td className="py-2 pr-4">{r.unique_readers}</td>
                  <td className="py-2 pr-4">
                    {(Number(r.total_seconds_spent ?? 0) / 3600).toFixed(1)} h
                  </td>
                  <td className="py-2 pr-4">{r.avg_seconds_per_session}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="mt-8 font-sans text-xs text-ink/40">
        {range === 'all'
          ? 'Analytics refresh nightly. Figures reflect the most recent refresh.'
          : 'Windowed figures are live; All time refreshes nightly.'}
      </p>
    </div>
  );
}

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 90, label: '90 days' },
  { value: 30, label: '30 days' },
  { value: 7, label: '7 days' },
];

function RangeSelector({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex rounded-md border border-ink/15 bg-parchment p-0.5" role="group">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`rounded px-3 py-1.5 font-sans text-sm ${
            value === o.value ? 'bg-ink text-parchment' : 'text-ink/60 hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-ink/10 bg-parchment p-5">
      <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink/50">{label}</p>
      <p className="mt-2 font-serif text-3xl text-ink">{value}</p>
    </div>
  );
}
